import { addDays, endOfDay, isSameDay } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { Habit } from 'shared/types/habit';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { v4 as uuidv4 } from 'uuid';

import * as Styled from './reschedule-button.style';

export type RescheduleButtonProps = {
  tasks: Task[];
};

export const RescheduleButton: React.FC<RescheduleButtonProps> = React.memo(
  ({ tasks }) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const today = useToday();
    const user = useUser();
    const { menuOpen, openMenu, closeMenu } = useOpenMenu();

    const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

    const { submit: updateTask } = useUpdateTask();

    const onChangeDate = (date?: Date) => {
      if (!date) {
        return;
      }

      tasks.forEach((task) => {
        // tasks
        if (task.type === TaskType.Task) {
          updateTask({
            id: task.id,
            endStrategy: {
              completionCount: task.endStrategy?.completionCount ?? null,
              deadline: endOfDay(date),
            },
          });
          return;
        }

        // habits
        const schedules = structuredClone((task as Habit).schedules ?? []);

        const activeSchedule = getActiveSchedule(schedules, today);
        if (!activeSchedule) {
          return;
        }

        const skipDate = addDays(date, -1);
        let entryOnDate = activeSchedule.entries.find(({ date }) =>
          isSameDay(date, skipDate),
        );
        if (!entryOnDate) {
          entryOnDate = {
            id: uuidv4(),
            date: skipDate,
            state: ScheduleEntryState.Skip,
            value: null,
          };

          activeSchedule.entries.push(entryOnDate);
        }

        entryOnDate.state = ScheduleEntryState.Skip;
        entryOnDate.value = null;

        updateTask({ id: task.id, schedules });
      });

      closeMenu();
    };

    return (
      <Styled.Container onClick={stopPropagation} ref={containerRef}>
        <Button
          variant={Variants.Text}
          size={Sizes.Small}
          color={Colors.Inherit}
          onClick={openMenu}
        >
          {t('general.sections.overdue.reschedule.button.label')}
        </Button>

        {menuOpen && (
          <DatepickerWithOptions
            referenceElement={containerRef}
            value={undefined}
            onChange={onChangeDate}
            dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
            weekStartsOn={getWeekStartsOn(user)}
            icon={IconOptions.Calendar}
            onOutside={closeMenu}
          />
        )}
      </Styled.Container>
    );
  },
);
